import { motion } from "framer-motion";

const TEXTURE_BG =
  "https://images.unsplash.com/photo-1771930587780-188872b98bd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBkYXJrJTIwZ3JlZW4lMjB0ZXh0dXJlfGVufDB8fHx8MTc3ODM0MzI1N3ww&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function About() {
  return (
    <section
      id="ethos"
      data-testid="about-section"
      className="relative py-32 sm:py-40 lg:py-48 overflow-hidden"
    >
      {/* Subtle texture background */}
      <div
        className="absolute inset-0 opacity-[0.07] bg-cover bg-center"
        style={{ backgroundImage: `url(${TEXTURE_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />

      <div className="relative max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Left label column */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="md:col-span-4"
          >
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#155E63] font-bold mb-6">
              The Ethos
            </p>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#F5F2EA]">
              A word borrowed from Latin.
            </h2>
            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="font-display italic text-2xl text-[#F5F2EA]/80 leading-snug">
                ae·vum
              </p>
              <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">
                <span className="text-[#F5F2EA]">noun.</span> an age, a lifetime,
                eternity — a length of time without measure.
              </p>
            </div>
          </motion.div>

          {/* Right body column */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            custom={1}
            className="md:col-span-8 md:pl-12 md:border-l md:border-white/10"
          >
            <div className="space-y-8 text-lg sm:text-xl text-[#F5F2EA]/85 leading-[1.7] font-light">
              <p>
                Aevum is a clothing house engineered for the people who do not
                quit. We build athletic garments for an existence that does not
                end at the finish line — but continues, mile after mile, hour
                after hour, year after year.
              </p>
              <p>
                We believe in the long arc. The early mornings nobody sees. The
                quiet repetitions. The version of yourself you have not become
                yet, but already are inside.
              </p>
              <p className="text-[#F5F2EA]">
                Time is not your opponent. Time is the material we cut from.
              </p>
            </div>

            {/* Stat row */}
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 gap-8 pt-10 border-t border-white/10">
              {[
                { k: "01.", t: "Ultralight technical knit", s: "Engineered yarn" },
                { k: "02.", t: "Wear-tested at altitude", s: "From sea to summit" },
                { k: "03.", t: "Made to outlive trends", s: "Endless season" },
              ].map((s) => (
                <div key={s.k} data-testid={`ethos-stat-${s.k.replace(".", "")}`}>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-[#155E63] font-bold">
                    {s.k}
                  </p>
                  <p className="mt-3 font-display text-xl text-[#F5F2EA] leading-tight">
                    {s.t}
                  </p>
                  <p className="mt-2 text-xs text-[#A3A3A3] tracking-wide">
                    {s.s}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
